import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import * as Yup from "yup";
import FormError from "../../Tags/FormError";
import { updateTeamProductAccessSettings } from "../../../apis/workspace/teams";
import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import Image from "../../Tags/Image";
import Input from "../../Tags/Input";
import { Button } from "../../ui/button";
import { useParams } from "react-router-dom";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function UpdateProductSettingsModal({
  closeModal,
  data = {},
  activeWorkspace,
  refetch = () => undefined,
}) {
  const captureEvent = useCaptureEvent();
  const { teamId } = useParams();
  const queryClient = useQueryClient();

  const formInitialValues = useMemo(() => {
    const defaultCreditLimitType = "Team";
    const defaultCreditResetTimeline = "daily";

    return {
      credit_limit_value: data?.credit_limit_value ?? "",
      credit_limit_type: data?.credit_limit_type ?? defaultCreditLimitType,
      credit_reset_timeline: data?.credit_reset_timeline ?? defaultCreditResetTimeline,
    };
  }, [data?.credit_limit_value, data?.credit_limit_type, data?.credit_reset_timeline]);

  const handleModalClose = () => {
    formik.resetForm();
    closeModal();
  };

  // handle create team
  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      credit_limit_value: Yup.string()
        .matches(/^\d*$/, "Only numbers are allowed")
        .required("Please enter credit limit value"),
      credit_limit_type: Yup.string().required("Please select credit limit type"),
      credit_reset_timeline: Yup.string().required("Please select credit reset timeline"),
    }),
    onSubmit: (values) => {
      const payload = {
        credit_limit_value: values.credit_limit_value,
        credit_limit_type: values.credit_limit_type,
        credit_reset_timeline: values.credit_reset_timeline,
      };
      updateTeamProductAccessSettingsFn.mutate({
        data: payload,
        teamId: teamId,
        productId: data && data.metric_name,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
    },
  });

  const handleCreditLimitChange = useCallback(
    (event) => {
      const { value } = event.target;
      if (value === "" || /^\d+$/.test(value)) {
        formik.setFieldValue("credit_limit_value", value);
      }
    },
    [formik]
  );

  const updateTeamProductAccessSettingsFn = useMutation(
    "update_team_product_setting",
    updateTeamProductAccessSettings,
    {
      onSuccess(data, variables, context) {
        toast.success(data?.message);
        captureEvent("SET_TeamDetails_UpdateCreditLimitSuccess", {
          team_id: teamId,
          product_name: variables?.productId,
          credit_limit_value: formik.values.credit_limit_value,
          credit_limit_type: formik.values.credit_limit_type,
          credit_reset_timeline: formik.values.credit_reset_timeline,
        });
        handleModalClose();
        queryClient.invalidateQueries("team_products");
        formik.resetForm();
        formik.setSubmitting(false);
        if (refetch) {
          refetch();
        }
      },
      onError(error) {
        formik.setSubmitting(false);
      },
    }
  );
  return (
    <>
      <div className="flex items-center space-x-4 mb-2">
        <div className="inline-flex justify-center border border-border rounded-[--radius] p-2 bg-background">
          <Image src="/images/icons/untitledui/group.svg" width="20" className="inline-block" />
        </div>
        <div>
          <h2 className="text-base font-semibold leading-7 ">Set a credit limit</h2>
          <p className="text-paragraph">
            Set a limit to avoid unexpected usage for{" "}
            <span className="font-bold text-foreground"> {data?.metric_display_name}</span>
          </p>
        </div>
      </div>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-3">
            <div className="mb-5">
              <label htmlFor="credit_limit_value" className="block mb-2 text-sm font-medium leading-6">
                Credit limit
              </label>
              <Input
                type="text"
                name="credit_limit_value"
                id="credit_limit_value"
                autoComplete="credit_limit_value"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 1000"
                onChange={handleCreditLimitChange}
                onBlur={formik.handleBlur}
                value={formik.values.credit_limit_value}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Prevent default behavior of Enter key
                    formik.handleSubmit(); // Manually submit the form
                  }
                }}
              />
              <FormError field="name" />
            </div>

            <div className="mb-5">
              <label htmlFor="credit_limit_type" className="block mb-2 text-sm font-medium leading-6">
                Credit limit type
              </label>
              <select
                className="h-11 px-4 py-3 text-sm w-full rounded-[--radius] border border-border bg-background text-foreground"
                name="credit_limit_type"
                id="credit_limit_type"
                onChange={formik.handleChange}
                value={formik.values.credit_limit_type}
              >
                <option value="Team">Team</option>
                <option value="User">User</option>
              </select>

              <FormError field="credit_limit_type" />
            </div>
            <div className="mb-5">
              <label htmlFor="credit_reset_timeline" className="block mb-2 text-sm font-medium leading-6">
                Credit reset timeline
              </label>
              <select
                className="h-11 px-4 py-3 text-sm w-full rounded-[--radius] border border-border bg-background text-foreground"
                name="credit_reset_timeline"
                id="credit_reset_timeline"
                onChange={formik.handleChange}
                value={formik.values.credit_reset_timeline}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>

              <FormError field="credit_reset_timeline" />
            </div>
          </div>
          <div className="mt-0 space-x-2 flex">
            <Button
              className="flex-1"
              onClick={(e) => {
                e.preventDefault();
                handleModalClose();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formik.isSubmitting || formik.values.credit_limit_value === ""}
              className="flex-1"
            >
              Confirm
              {formik.isSubmitting ? <div className="loader_circle ml-3"></div> : ""}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </>
  );
}
